import React, { useEffect, useReducer } from 'react';
import {
  Button, Modal, SelectPicker, Stack,
} from 'rsuite';
import { ItemDataType } from 'rsuite/cjs/internals/types';
import { useStoreActions, useStoreState } from '../../../store/hooks';
import { holdOffers } from '../../../api/hold/hold';
import { showSuccessMessage } from '../../../utils/message';

interface Props {
  offerId: number;
  marketplace: string;
  close: () => void;
}

interface State {
  status: 'ready' | 'saving';
  reason: string | null;
}

const initialState: State = {
  status: 'ready',
  reason: null,
};

type ReasonSetAction = { type: 'reason/set', payload: string | null };
type HoldSaveAction = { type: 'hold/save' };
type Action = ReasonSetAction | HoldSaveAction;

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'reason/set':
      return { ...state, reason: action.payload };
    case 'hold/save':
      return { ...state, status: 'saving' };
    default:
      return state;
  }
}

function mapHoldReason(reason: string): ItemDataType<string> {
  return { label: reason, value: reason };
}

export default function ProductHoldDialog({
  offerId, marketplace, close,
}: Props): JSX.Element {
  const holdReasons = useStoreState(state => state.catalogs.holdReasons);
  const { fetchHoldReasons } = useStoreActions(actions => actions.catalogs);
  const { reloadProducts } = useStoreActions(actions => actions.products);
  const [state, dispatch] = useReducer(reducer, initialState);
  const { reason } = state;
  const isSaving = state.status === 'saving';

  useEffect(() => {
    const abortController = new AbortController();

    fetchHoldReasons(abortController.signal);

    return () => {
      abortController.abort();
    };
  }, []);

  async function handleHold(): Promise<void> {
    if (!reason) {
      return;
    }

    dispatch({ type: 'hold/save' });
    try {
      await holdOffers({ offers: [{ id: offerId, reason }] });
      await reloadProducts();
      showSuccessMessage('Offer was put on hold.');
    } finally {
      close();
    }
  }

  return (
    <Modal
      size="xs"
      open
      onClose={close}
    >
      <Modal.Header>
        <Modal.Title>Put offer on hold</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Stack
          direction="column"
          alignItems="stretch"
          spacing={10}
        >
          <p>
            {`The offer will be put on hold and hidden from ${marketplace}.`}
          </p>
          <SelectPicker
            block
            cleanable={false}
            searchable={false}
            data={holdReasons.map(mapHoldReason)}
            value={reason}
            onChange={value => dispatch({ type: 'reason/set', payload: value })}
            placeholder="Select hold reason"
          />
        </Stack>
      </Modal.Body>
      <Modal.Footer>
        <Button
          onClick={() => handleHold()}
          disabled={isSaving || !reason}
          loading={isSaving}
          appearance="primary"
        >
          Hold
        </Button>
        <Button
          onClick={close}
          disabled={isSaving}
          appearance="subtle"
        >
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
